import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import * as adminApi from '../api/admin/endpoints';
import * as businessApi from '../api/business/endpoints';
import { ApiRequestError } from '../api/errors';
import type { SessionKind } from '../api/SessionKind';
import { clearSession, readSession, writeSession } from '../api/storage';

import { SessionContext, type SessionState } from './SessionContext';
import type { SessionProfile } from './SessionProfile';

/**
 * The session: which API it belongs to, and the profile `/auth/me` answers.
 *
 * The profile is a query under `['me', kind]`, so a mutation that changes the user's own rights — creating
 * a company grants its creator every company permission — refreshes it by invalidating `me`.
 */
export const SessionProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<SessionKind | null>(() => readSession()?.kind ?? null);

  const me = useQuery<SessionProfile>({
    queryKey: ['me', kind],
    enabled: kind !== null,
    retry: false,
    staleTime: 60_000,
    queryFn: async (): Promise<SessionProfile> =>
      kind === 'platform'
        ? { kind: 'platform', user: await adminApi.readMe() }
        : { kind: 'business', user: await businessApi.readMe() },
  });

  useEffect(() => {
    // A rejected session ends here. A network failure does not: the tokens may be perfectly good.
    if (me.error instanceof ApiRequestError && (me.error.status === 401 || me.error.status === 403)) {
      clearSession();
      setKind(null);
    }
  }, [me.error]);

  const signIn = useCallback(
    async (next: SessionKind, email: string, password: string): Promise<void> => {
      if (next === 'platform') {
        const result = await adminApi.login(email, password);

        writeSession({
          kind: next,
          accessToken: result.tokens.accessToken,
          refreshToken: result.tokens.refreshToken,
        });
        queryClient.setQueryData<SessionProfile>(['me', next], { kind: 'platform', user: result.user });
      } else {
        const result = await businessApi.login(email, password);

        writeSession({
          kind: next,
          accessToken: result.tokens.accessToken,
          refreshToken: result.tokens.refreshToken,
        });
        queryClient.setQueryData<SessionProfile>(['me', next], { kind: 'business', user: result.user });
      }

      setKind(next);
    },
    [queryClient],
  );

  const signOut = useCallback(async (): Promise<void> => {
    try {
      if (kind === 'platform') {
        await adminApi.logout();
      } else if (kind === 'business') {
        await businessApi.logout();
      }
    } catch {
      // Signing out locally does not wait for the server: the tokens are dropped either way.
    } finally {
      clearSession();
      setKind(null);
      queryClient.clear();
    }
  }, [kind, queryClient]);

  const value = useMemo<SessionState>(
    () => ({
      profile: kind === null ? null : (me.data ?? null),
      isLoading: kind !== null && me.isLoading,
      error: kind === null ? null : me.error,
      retry: () => void me.refetch(),
      signIn,
      signOut,
    }),
    [kind, me, signIn, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
};
